import { Coupon, EnterpriseCheckout } from '@scribe-sas/types/admin'
import autoBind from 'auto-bind'
import engine from 'engine'
import { makeAutoObservable } from 'mobx'
import NotificationService from 'services/NotificationService'

export default class EnterpriseCheckoutsFormViewModel {
  private _enterpriseCheckoutUuid: string
  private _loading: boolean = false
  private _couponsLoading: boolean = false
  private _coupons: Coupon[] = []

  private _firstName: string = ''
  private _lastName: string = ''
  private _jobPosition: string = ''
  private _email: string = ''
  private _seatCount: number = 100
  private _plan: EnterpriseCheckout['plan'] = 'enterprise'
  private _billingInterval: EnterpriseCheckout['billing_interval'] = 'yearly'
  private _stripeCouponId: string = ''
  private _businessName: string = ''
  private _line1: string = ''
  private _line2: string = ''
  private _city: string = ''
  private _state: string = ''
  private _postalCode: string = ''
  private _country: string = ''
  private _emails: string[] = []
  private _currencyCode: EnterpriseCheckout['currency_code'] = 'USD'
  private _promotionCodeCode: string = ''

  private _firstNameError: string | null = null
  private _lastNameError: string | null = null
  private _jobPositionError: string | null = null
  private _emailError: string | null = null
  private _seatCountError: string | null = null
  private _planError: string | null = null
  private _billingIntervalError: string | null = null
  private _stripeCouponIdError: string | null = null
  private _businessNameError: string | null = null
  private _emailsError: string | null = null
  private _currencyCodeError: string | null = null
  private _promotionCodeCodeError: string | null = null

  constructor(enterpriseCheckoutUuid: string) {
    makeAutoObservable(this)
    autoBind(this)
    this._enterpriseCheckoutUuid = enterpriseCheckoutUuid
    this._firstName = this.enterpriseCheckout?.firstName || ''
    this._lastName = this.enterpriseCheckout?.lastName || ''
    this._jobPosition = this.enterpriseCheckout?.jobPosition || ''
    this._email = this.enterpriseCheckout?.email || ''
    this._seatCount = this.enterpriseCheckout?.seatCount || 100
    this._plan = this.enterpriseCheckout?.plan || 'enterprise'
    this._billingInterval = this.enterpriseCheckout?.billingInterval || 'yearly'
    this._businessName = this.enterpriseCheckout?.businessName || ''
    this._line1 = this.enterpriseCheckout?.line1 || ''
    this._line2 = this.enterpriseCheckout?.line2 || ''
    this._city = this.enterpriseCheckout?.city || ''
    this._state = this.enterpriseCheckout?.state || ''
    this._postalCode = this.enterpriseCheckout?.postalCode || ''
    this._country = this.enterpriseCheckout?.country || ''
    this._emails = this.enterpriseCheckout?.emails || []
    this._stripeCouponId = this.enterpriseCheckout?.stripeCouponId || ''
    this._currencyCode = this.enterpriseCheckout?.currencyCode || 'USD'
    this._promotionCodeCode = this.enterpriseCheckout?.promotionCodeCode || ''
  }

  public get enterpriseCheckout() {
    return engine.enterpriseCheckoutsEngine.forUuid(
      this._enterpriseCheckoutUuid,
    )
  }

  public async getCoupons() {
    this._couponsLoading = true
    const response = await engine.enterpriseCheckoutsEngine.getCoupons()
    if (response && response.outcome === 'success') {
      this._coupons = response.data.coupons
    }
    this._couponsLoading = false
  }

  public get couponsLoading() {
    return this._couponsLoading
  }

  public get promotionCodeCode() {
    return this._promotionCodeCode
  }

  public setPromotionCodeCode(promotionCodeCode: string) {
    this._promotionCodeCode = promotionCodeCode
  }

  public get loading() {
    return this._loading
  }

  public get firstName() {
    return this._firstName
  }

  public setFirstName(firstName: string) {
    this._firstName = firstName
  }

  public get lastName() {
    return this._lastName
  }

  public setLastName(lastName: string) {
    this._lastName = lastName
  }

  public get jobPosition() {
    return this._jobPosition
  }

  public setJobPosition(jobPosition: string) {
    this._jobPosition = jobPosition
  }

  public get email() {
    return this._email
  }

  public setEmail(email: string) {
    this._email = email
  }

  public get seatCount() {
    return this._seatCount
  }

  public setSeatCount(seatCount: number) {
    this._seatCount = seatCount
  }

  public get plan() {
    return this._plan
  }

  public setPlan(plan: EnterpriseCheckout['plan']) {
    this._plan = plan
  }

  public get billingInterval() {
    return this._billingInterval
  }

  public setBillingInterval(
    billingInterval: EnterpriseCheckout['billing_interval'],
  ) {
    this._billingInterval = billingInterval
  }

  public get stripeCouponId() {
    return this._stripeCouponId
  }

  public setStripeCouponId(stripeCouponId: string | null) {
    this._stripeCouponId = stripeCouponId || ''
  }

  public get businessName() {
    return this._businessName
  }

  public setBusinessName(businessName: string) {
    this._businessName = businessName
  }

  public get line1() {
    return this._line1
  }

  public setLine1(line1: string) {
    this._line1 = line1
  }

  public get line2() {
    return this._line2
  }

  public setLine2(line2: string) {
    this._line2 = line2
  }

  public get city() {
    return this._city
  }

  public setCity(city: string) {
    this._city = city
  }

  public get state() {
    return this._state
  }

  public setState(state: string) {
    this._state = state
  }

  public get postalCode() {
    return this._postalCode
  }

  public setPostalCode(postalCode: string) {
    this._postalCode = postalCode
  }

  public get country() {
    return this._country
  }

  public setCountry(country: string | null) {
    this._country = country || ''
  }

  public get emails() {
    return this._emails
  }

  public setEmails(emails: string[]) {
    this._emails = emails
  }

  public get businessNameError() {
    return this._businessNameError
  }

  public get emailsError() {
    return this._emailsError
  }

  public get firstNameError() {
    return this._firstNameError
  }

  public get lastNameError() {
    return this._lastNameError
  }

  public get jobPositionError() {
    return this._jobPositionError
  }

  public get emailError() {
    return this._emailError
  }

  public get seatCountError() {
    return this._seatCountError
  }

  public get planError() {
    return this._planError
  }

  public get billingIntervalError() {
    return this._billingIntervalError
  }

  public get stripeCouponIdError() {
    return this._stripeCouponIdError
  }

  public get coupons() {
    return this._coupons
  }

  public get currencyCode() {
    return this._currencyCode
  }

  public setCurrencyCode(currencyCode: EnterpriseCheckout['currency_code']) {
    this._currencyCode = currencyCode
  }

  public get currencyCodeError() {
    return this._currencyCodeError
  }

  public get promotionCodeCodeError() {
    return this._promotionCodeCodeError
  }

  public async save(onSuccess?: () => void) {
    if (!this.enterpriseCheckout) {
      return
    }
    this._loading = true
    this._firstNameError = null
    this._lastNameError = null
    this._jobPositionError = null
    this._emailError = null
    this._seatCountError = null
    this._planError = null
    this._billingIntervalError = null
    this._stripeCouponIdError = null
    this._businessNameError = null
    this._currencyCodeError = null

    const attributes = {
      uuid: this.enterpriseCheckout.uuid,
      first_name: this.firstName,
      last_name: this.lastName,
      job_position: this.jobPosition,
      email: this.email,
      seat_count: this.seatCount,
      plan: this.plan,
      billing_interval: this.billingInterval,
      stripe_coupon_id: this.stripeCouponId,
      stripe_promotion_code_code: this.promotionCodeCode,
      business_name: this.businessName,
      line_1: this.line1,
      line_2: this.line2,
      city: this.city,
      state: this.state,
      postal_code: this.postalCode,
      country: this.country,
      emails: this.emails,
      currency_code: this.currencyCode || 'USD',
    }
    const response = await this.enterpriseCheckout.save(attributes)
    if (response && response.outcome === 'success') {
      NotificationService.success('Enterprise checkout saved')
      if (onSuccess) {
        onSuccess()
      }
    } else if (response) {
      NotificationService.error(
        'An error occurred while saving the enterprise checkout',
      )
      this._firstNameError = response.data.errors?.first_name?.message || null
      this._lastNameError = response.data.errors?.last_name?.message || null
      this._emailError = response.data.errors?.email?.message || null
      this._seatCountError = response.data.errors?.seat_count?.message || null
      this._planError = response.data.errors?.plan?.message || null
      this._billingIntervalError =
        response.data.errors?.billing_interval?.message || null
      this._businessNameError =
        response.data.errors?.business_name?.message || null
      this._currencyCodeError =
        response.data.errors?.currency_code?.message || null
      this._promotionCodeCodeError =
        response.data.errors?.stripe_promotion_code_code?.message || null
    }
    this._loading = false
  }
}
